import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { BubbleMenu, EditorContent, useEditor } from '@tiptap/react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useMergeRefs } from '@/js/libs/hooks/useMergeRefs';
import { editorProps, extensions } from './constants';
import { LinkTooltip } from './LinkTooltip';
import { Textarea, TextareaWrapper, Wrapper } from './styled';
import { Toolbar } from './Toolbar';
export const RichTextEditor = forwardRef(function RichTextEditor({ value, onChange, disabled, success, warning, error, readOnly, withPreviewToggle, placeholders, linkTooltipPlaceholder = 'Enter a URL', additionalToolbarItems, dataTestId, rows, ...props }, ref) {
    const [isPreview, setIsPreview] = useState(true);
    const [rawEditorValue, setRawEditorValue] = useState(value ?? '');
    const rawEditorRef = useRef(null);
    const rawEditorMergedRefs = useMergeRefs(ref, rawEditorRef);
    const editor = useEditor({
        extensions,
        onBlur: (p) => {
            const html = p.editor.getHTML();
            onChange?.(html);
            setRawEditorValue(html);
        },
        parseOptions: {
            preserveWhitespace: 'full',
        },
        editorProps,
    });
    const handleRawEditorChange = useCallbackRef((e) => {
        setRawEditorValue(e.target.value);
    });
    const handleSaveLink = useCallbackRef((href) => {
        editor?.chain().focus().extendMarkRange('link').setLink({ href }).run();
    });
    const shouldShowLinkTooltip = () => !!editor?.isActive('link');
    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value ?? '');
        }
    }, [editor, value]);
    useEffect(() => {
        if (!editor)
            return;
        if (!readOnly && !disabled) {
            editor.setOptions({ editable: true });
        }
        else {
            editor.setOptions({ editable: false });
        }
    }, [editor, readOnly, disabled]);
    // @ts-expect-error typescript doesn't know about the focus and select commands on contenteditable divs
    useImperativeHandle(ref, () => ({
        focus: () => {
            if (editor) {
                editor.commands.focus();
            }
        },
        select: () => {
            if (editor) {
                editor.commands.focus();
                editor.commands.selectAll();
            }
        },
    }));
    return (_jsxs(Wrapper, { inputDisabled: readOnly || disabled, success: success, warning: warning, error: error, rows: rows, "data-testid": dataTestId || 'rich-text-editor', children: [editor && !readOnly && (_jsx(Toolbar, { editor: editor, disabled: disabled || readOnly, placeholders: placeholders, additionalToolbarItems: additionalToolbarItems, isPreview: isPreview, setIsPreview: setIsPreview, withPreviewToggle: withPreviewToggle, rawEditorValue: rawEditorValue, setRawEditorValue: setRawEditorValue, rawEditorRef: rawEditorRef, dataTestId: dataTestId || 'rich-text-editor' })), _jsx(EditorContent, { editor: editor, "data-visible": isPreview, innerRef: ref, "data-testid": `${dataTestId || 'rich-text-editor'}-editor-content`, ...props }), _jsx(TextareaWrapper, { "data-replicated-value": rawEditorValue, "data-visible": !isPreview, children: _jsx(Textarea, { value: rawEditorValue, onChange: handleRawEditorChange, ref: rawEditorMergedRefs, "data-testid": `${dataTestId || 'rich-text-editor'}-raw-editor-textarea`, ...props }) }), editor && !readOnly && (_jsx(BubbleMenu, { editor: editor, shouldShow: shouldShowLinkTooltip, children: _jsx(LinkTooltip, { editor: editor, saveLink: handleSaveLink, placeholder: linkTooltipPlaceholder, dataTestId: dataTestId || 'rich-text-editor' }) }))] }));
});
